import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, TriangleAlert } from 'lucide-react';
import { getCatalog, type CatalogEntry, type CatalogResult } from '../lib/library';

type Load = { kind: 'loading' } | CatalogResult;

/**
 * Spec 008 — the heritage library catalog (SC-043). Public, read-only. Groups the 23 thematic documents and
 * the 7 translated primary sources; each entry links to its reading view. Four states: loading → success →
 * empty ("no documents archived") → error/unavailable. Editorial column matching Manifest.tsx; the two-digit
 * chapter numbers are shown because the archive IS an ordered sequence, so the order carries real meaning.
 */
export default function Library() {
  const [state, setState] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    getCatalog().then((r) => {
      if (active) setState(r);
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <article className="mx-auto w-full max-w-prose px-6 py-16 sm:py-20">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">
        Heritage library
      </p>
      <h1 className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink sm:text-4xl">
        The archive
      </h1>
      <p className="mt-4 font-serif text-xl italic text-muted">
        The order’s own seized and published writings on the historical Bavarian Illuminati (1776–1787), kept
        verbatim. History, not conspiracy — read the sources for yourself.
      </p>
      <hr className="my-10 border-border" />

      {state.kind === 'loading' && (
        <div
          className="flex items-center gap-3 font-sans text-sm text-muted"
          role="status"
          aria-live="polite"
        >
          <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
          Loading the archive…
        </div>
      )}

      {(state.kind === 'unavailable' || state.kind === 'error') && (
        <div
          className="flex items-start gap-3 rounded-lg border border-border bg-surface p-6"
          role="alert"
        >
          <TriangleAlert aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-accent-deep" />
          <div>
            <h2 className="font-display text-sm font-semibold uppercase tracking-[0.12em] text-ink">
              {state.kind === 'unavailable' ? 'The archive is not available' : 'Couldn’t load the archive'}
            </h2>
            <p className="mt-2 font-serif text-lg leading-relaxed text-ink">
              {state.kind === 'unavailable'
                ? 'The heritage archive isn’t reachable right now. Nothing else on the site is affected — try again shortly.'
                : 'We couldn’t reach the server to load the catalog. Check your connection and try again.'}
            </p>
          </div>
        </div>
      )}

      {state.kind === 'ok' && state.data.thematic.length + state.data.primary.length === 0 && (
        <p className="font-serif text-lg leading-relaxed text-muted">
          No documents have been archived yet. Check back soon.
        </p>
      )}

      {state.kind === 'ok' && state.data.thematic.length + state.data.primary.length > 0 && (
        <div className="space-y-12">
          <Group heading="Thematic documents" entries={state.data.thematic} />
          <Group heading="Primary sources" entries={state.data.primary} />
        </div>
      )}
    </article>
  );
}

function Group({ heading, entries }: { heading: string; entries: CatalogEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <section>
      <h2 className="font-display text-lg font-semibold uppercase tracking-[0.12em] text-ink">{heading}</h2>
      <ol className="mt-5 divide-y divide-border border-t border-border">
        {entries.map((e) => (
          <li key={e.slug}>
            <Link
              to={`/library/${e.slug}`}
              className="group flex gap-4 py-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <span
                aria-hidden
                className="mt-1 select-none font-sans text-sm font-semibold tabular-nums text-accent-deep"
              >
                {String(e.orderKey).padStart(2, '0')}
              </span>
              <span className="min-w-0">
                <span className="block font-serif text-xl leading-snug text-ink underline decoration-transparent decoration-1 underline-offset-4 transition-colors group-hover:decoration-accent">
                  {e.title}
                </span>
                {e.descriptor && (
                  <span className="mt-1 block font-serif text-base leading-relaxed text-muted">
                    {e.descriptor}
                  </span>
                )}
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
}
